import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface RegionItem {
  uuid: string
  branchName: string | null
  name: string
  userCount: number
}

export interface TeamItem {
  uuid: string
  regionUuid?: string
  branchName: string
  name: string
  userCount: number
}

export interface AgencyItem {
  uuid: string
  teamUuid?: string
  branchName: string
  name: string
  userCount: number
}

const base = 'users/admin/organizations'

export const useOrganizationAdminStore = defineStore('organizationAdmin', () => {
  const suiteStore = useSuiteStore()
  const regions = ref<RegionItem[]>([])
  const teams = ref<TeamItem[]>([])
  const agencies = ref<AgencyItem[]>([])

  async function fetchRegions(): Promise<RegionItem[]> {
    const res = await suiteStore.suiteFetch(`${base}/regions`, { method: 'GET', credentials: 'include' })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      regions.value = json ?? []
      return regions.value
    }
    return []
  }

  async function fetchTeams(): Promise<TeamItem[]> {
    const res = await suiteStore.suiteFetch(`${base}/teams`, { method: 'GET', credentials: 'include' })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      teams.value = json ?? []
      return teams.value
    }
    return []
  }

  async function fetchAgencies(): Promise<AgencyItem[]> {
    const res = await suiteStore.suiteFetch(`${base}/agencies`, { method: 'GET', credentials: 'include' })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      agencies.value = json ?? []
      return agencies.value
    }
    return []
  }

  async function createRegion(dto: { bu?: string | null; name: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/regions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchRegions()
      return true
    }
    return false
  }

  async function updateRegion(uuid: string, dto: { bu?: string | null; name?: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/regions/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchRegions()
      return true
    }
    return false
  }

  async function createTeam(dto: { regionUuid: string; name: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/teams`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchTeams()
      return true
    }
    return false
  }

  async function updateTeam(uuid: string, dto: { regionUuid?: string; name?: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/teams/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchTeams()
      return true
    }
    return false
  }

  async function createAgency(dto: { teamUuid: string; name: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/agencies`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchAgencies()
      return true
    }
    return false
  }

  async function updateAgency(uuid: string, dto: { teamUuid?: string; name?: string }): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`${base}/agencies/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      await fetchAgencies()
      return true
    }
    return false
  }

  return {
    regions,
    teams,
    agencies,
    fetchRegions,
    fetchTeams,
    fetchAgencies,
    createRegion,
    updateRegion,
    createTeam,
    updateTeam,
    createAgency,
    updateAgency,
  }
})
